import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import AddButtonInTopBar from "../components/AddButtonInTopBar";
import BreadCrumb from "../components/breadCrumb";
import ComboBox from "../components/common/ComboBox";
import ComboBoxData from "../components/common/ComboBoxData";
import { ConfirmationPopup } from "../components/common/ConfirmationPopup";
import DataGrid from "../components/common/DataGrid";
import PageContent from "../components/pageContent";
import TopBarWithBreadCrumb from "../components/TopBarWithBreadCrumb";
import { FormatString } from "../helpers/stringHelpers";
import { checkCrud } from "../helpers/userRolesHelper";
import ColorPopup from "../popups/ColorPopup";
import ColorProductsPopup from "../popups/ColorProductsPopup";
import FileUploadPopup from "../popups/FileUploadPopup";
import SampleCardPopup from "../popups/SampleCardPopup";
import { TranslationProvider } from "../providers/TranslationContext";
import { addNotification } from "../redux/actions";
import { apiDeleteColor, apiGetAllColorsForSampleCard, apiGetColorReceiptsCount, apiUpdatePictureName } from "../services/ColorsService";
import { apiGetAllProductsForColor } from "../services/ProductsService";
import { apiGetAllSampleCards } from "../services/SampleCardsService";

interface Props {
  contextLevel: string;
}

const entityName = 'color';
const translationPrefix = 'Colors';
const translationModel = { prefix: translationPrefix };

const OdstinyPage = (props: Props) => {
  const { t } = useTranslation(undefined, { keyPrefix: translationPrefix });
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ rows: [] });

  const [sampleCards, setSampleCards] = useState([]);
  const [sampleCard, setSampleCard] = useState<number>();
  const [sampleCardName, setSampleCardName] = useState<number>();
  const [productsPopupData, setProductsPopupData] = useState({
    visible: false,
    id: undefined,
    products: [],
  });

  const [fileUploadPopup, setFileUploadPopup] = useState({
    visible: false,
    id: undefined,
    name: undefined
  });

  const [colorPopupData, setColorPopupData] = useState({
    visible: false,
    id: undefined,
  });

  const [deleteWithReceipts, setDeleteWithReceipts] = useState<{ row: any; receiptCount: number } | null>(null);

  const dispatch = useDispatch();

  const [canCreate, setCanCreate] = useState(false);
  const [canRead, setCanRead] = useState(false);
  const [canUpdate, setCanUpdate] = useState(false);
  const [canDelete, setCanDelete] = useState(false);

  const userInfo = useSelector((state: any) => state.userInfo);

  const setRoles = () => {
    setCanCreate(checkCrud(entityName, 'create', userInfo));
    setCanRead(checkCrud(entityName, 'read', userInfo));
    setCanUpdate(checkCrud(entityName, 'update', userInfo));
    setCanDelete(checkCrud(entityName, 'delete', userInfo));
  }

  useEffect(() => {
    setRoles();
  }, [userInfo.UserRoles])

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    let sampleCardsResponse = await apiGetAllSampleCards();
    let sampleCardsData = sampleCardsResponse.data;

    if (sampleCardsData && sampleCardsData.length) {
      const sampleCartId = sampleCard ?? sampleCardsData[0].id;
      let colorsResponse = await loadColors(sampleCartId);

      setSampleCards(sampleCardsData);
      setSampleCard(sampleCardsData[0].id);
      setSampleCardName(sampleCardsData[0].name);
      setData({ rows: colorsResponse.data });
      setLoading(false);
    } else {
      setLoading(false);
    }
  };

  const loadColors = async (sampleCardId: number) => {
    let colorsResponse = await apiGetAllColorsForSampleCard(
      sampleCardId
    );
    return colorsResponse;
  }

  let handleSampleCardChange = async (value: any) => {
    setLoading(true);

    let colorsResponse = await apiGetAllColorsForSampleCard(value);
    setData({ rows: colorsResponse.data });
    setLoading(false);
    setSampleCard(value);
    const selectedSampleCard = sampleCards.find((s: any) => { return s.id === parseInt(value) }) as any;
    if (selectedSampleCard) {
      setSampleCardName(selectedSampleCard.name);
    }
  };

  // Obnova dat bez přepnutí do stavu "Načítám" – grid zůstane vykreslený,
  // takže uživatel zůstane na stejném místě ve scrollu i na stejné stránce.
  const refreshColors = async () => {
    if (!sampleCard) {
      return;
    }

    const colorsResponse = await apiGetAllColorsForSampleCard(sampleCard);
    setData({ rows: colorsResponse.data });
  };

  const handleDeleteRow = (row: any) => {
    apiGetColorReceiptsCount(row.id)
      .then((response: any) => {
        setDeleteWithReceipts({ row: row, receiptCount: response.data.receiptCount });
      })
      .catch(() => {
        dispatch(addNotification({ text: FormatString(t('Popup.DeleteFailure'), row.name), type: 'error' }));
      });
  };

  const deleteColor = (row: any) => {
    apiDeleteColor(row.id, true)
      .then(() => {
        dispatch(addNotification({ text: FormatString(t('Popup.DeleteSuccess'), row.name), type: 'success' }));
        refreshColors();
      })
      .catch(() => {
        dispatch(addNotification({ text: FormatString(t('Popup.DeleteFailure'), row.name), type: 'error' }));
      });
  };

  let handleFetchProductsForColor = async (value: any) => {
    let productsResponse = await apiGetAllProductsForColor(value);

    setProductsPopupData({
      visible: true,
      id: value,
      products: productsResponse.data,
    });
  };

  const handlePictureUploadError = (ex: any, pictureName: any) => {
    let colorId = fileUploadPopup.id;
    dispatch(addNotification({ text: FormatString(t('Popup.UploadPictureFailure'), pictureName, `${colorId}`), type: 'error' }));
    setFileUploadPopup({
      visible: false,
      id: undefined,
      name: undefined
    });
  };

  const handlePictureUpload = (pictureName: any) => {
    let colorId = fileUploadPopup.id;
    if (colorId) {
      apiUpdatePictureName(colorId, pictureName)
        .then((resp) => {
          dispatch(addNotification({ text: FormatString(t('Popup.UploadPictureSuccess'), pictureName, `${colorId}`), type: 'success' }));
          //loadData();
          if (sampleCard) {
            loadColors(sampleCard)
              .then((resp: any) => {
                setData({ rows: resp.data });
              });
          }
        })
        .catch((ex: any) => {
          dispatch(addNotification({ text: FormatString(t('Popup.UploadPictureFailure'), pictureName, `${colorId}`), type: 'error' }));
        })
        .finally(() => {
          setFileUploadPopup({
            visible: false,
            id: undefined,
            name: undefined
          });
        })
    }
  };

  return (
    <TranslationProvider value={translationModel}>
      {deleteWithReceipts && (
        <ConfirmationPopup
          header={t('Popup.DeleteWithReceiptsHeader')}
          text={FormatString(t('Popup.DeleteWithReceiptsMessage'), deleteWithReceipts.row.name, `${deleteWithReceipts.receiptCount}`)}
          handleConfirmation={() => {
            const row = deleteWithReceipts.row;
            setDeleteWithReceipts(null);
            deleteColor(row);
          }}
          handleClose={() => {
            setDeleteWithReceipts(null);
          }}
        />
      )}
      {fileUploadPopup.visible &&
        <FileUploadPopup
          id={fileUploadPopup.id}
          onSuccess={handlePictureUpload}
          onError={handlePictureUploadError}
          onCancel={() => {
            setFileUploadPopup({ visible: false, id: undefined, name: undefined });
          }}
        />
      }
      {colorPopupData.visible && (
        <ColorPopup
          id={colorPopupData.id}
          refreshData={() => { refreshColors() }}
          handleCancel={() => {
            setColorPopupData({ visible: false, id: undefined });
          }}
          sampleCardId={sampleCard}
          sampleCardName={sampleCardName}
        />
      )}
      {productsPopupData.visible && (
        <ColorProductsPopup
          id={colorPopupData.id}
          refreshData={() => { refreshColors() }}
          handleCancel={() => {
            setProductsPopupData({
              visible: false,
              id: undefined,
              products: [],
            });
          }}
          products={productsPopupData.products}
        />
      )}
      <TopBarWithBreadCrumb
        contextLevel={props.contextLevel}
        pageLevel="Odstíny"
        rightAreaComponent={
          <AddButtonInTopBar
            setPopupData={setColorPopupData}
            text={t('CreateText')}
            enabled={canCreate}
          />
        }
      />

      <PageContent>
        <>
          <ComboBoxData
            id="sampleCard"
            label={t('Fields.sampleCardName')}
            dataTextField="name"
            dataValueField="id"
            handleChange={handleSampleCardChange}
            rows={sampleCards}
          />

          <DataGrid
            loading={loading}
            rows={data.rows}
            resetKey={sampleCard}
            showDeleteButton={canDelete}
            showEditButton={canUpdate}
            handleEditAction={(id: any) => {
              setColorPopupData({ visible: true, id: id });
            }}
            //showCustomButton={true}
            setPictureUploadPopupData={canUpdate ? (id: any, name: any) => {
              setFileUploadPopup({
                visible: true,
                id: id,
                name: name
              });
            } : undefined}
            onDeleteRow={handleDeleteRow}
            skipDeleteConfirmation={true}
            //customButtonText="Natónovat"
            //handleCustomAction={handleFetchProductsForColor}
            cols={[
              { name: t('Fields.name'), dataField: "name", filterable: true, filterType: "text" },
              { name: t('Fields.colorName'), dataField: "colorName" },
              { name: t('Fields.noteInternal'), dataField: "noteInternal" }
            ]}
          />
        </>
      </PageContent>
    </TranslationProvider>
  );
};

export default OdstinyPage;
